<script setup>
import { computed, watch } from 'vue';
import Input from './Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { useI18n } from 'vue-i18n';
import { DURATION_UNITS } from './constants';

const props = defineProps({
  customUnitSelect: { type: Boolean, default: false },
  min: { type: Number, default: 0 },
  max: { type: Number, default: Infinity },
  disabled: { type: Boolean, default: false },
});

defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const unitOptions = computed(() => [
  {
    value: DURATION_UNITS.MINUTES,
    label: t('DURATION_INPUT.MINUTES'),
  },
  {
    value: DURATION_UNITS.HOURS,
    label: t('DURATION_INPUT.HOURS'),
  },
  {
    value: DURATION_UNITS.DAYS,
    label: t('DURATION_INPUT.DAYS'),
  },
]);
const duration = defineModel('modelValue', { type: Number, default: null });
const unit = defineModel('unit', {
  type: String,
  default: DURATION_UNITS.MINUTES,
  validate(value) {
    return Object.values(DURATION_UNITS).includes(value);
  },
});

const convertToMinutes = newValue => {
  if (unit.value === DURATION_UNITS.MINUTES) {
    return Math.floor(newValue);
  }
  if (unit.value === DURATION_UNITS.HOURS) {
    return Math.floor(newValue) * 60;
  }
  return Math.floor(newValue) * 24 * 60;
};

const transformedValue = computed({
  get() {
    if (duration.value == null) return null;
    if (unit.value === DURATION_UNITS.MINUTES) return duration.value;
    if (unit.value === DURATION_UNITS.HOURS)
      return Math.floor(duration.value / 60);
    if (unit.value === DURATION_UNITS.DAYS)
      return Math.floor(duration.value / 24 / 60);

    return 0;
  },
  set(newValue) {
    if (newValue == null || newValue === '') {
      duration.value = null;
      return;
    }
    duration.value = convertToMinutes(newValue);
  },
});

const normalizeDuration = () => {
  if (duration.value == null) return;

  duration.value = Math.min(Math.max(duration.value, props.min), props.max);
};

// when unit is changed set the nearest value to that unit
// so if the minute is set to 900, and the user changes the unit to "days"
// the transformed value will show 0, but the real value will still be 900
// this might create some confusion, especially when saving
// this watcher fixes it by rounding the duration basically, to the nearest unit value
watch(unit, () => {
  if (duration.value == null) return;
  let adjustedValue = convertToMinutes(transformedValue.value);
  duration.value = Math.min(Math.max(adjustedValue, props.min), props.max);
});
</script>

<template>
  <Input
    v-model="transformedValue"
    v-bind="$attrs"
    :aria-label="
      $attrs['aria-label'] || $attrs.label || t('DURATION_INPUT.PLACEHOLDER')
    "
    :size="customUnitSelect ? 'sm' : 'md'"
    type="number"
    autocomplete="off"
    :disabled="disabled"
    :placeholder="t('DURATION_INPUT.PLACEHOLDER')"
    :class="customUnitSelect ? 'min-w-0 flex-1' : 'flex-grow w-full'"
    @blur="normalizeDuration"
    @keydown.enter="normalizeDuration"
  />
  <ComboBox
    v-if="customUnitSelect"
    v-model="unit"
    class="basis-32 max-w-32 shrink-0"
    trigger-class="h-8"
    :aria-label="t('DURATION_INPUT.UNIT')"
    :options="unitOptions"
    :disabled="disabled"
    :allow-deselect="false"
    teleport
  />
  <select
    v-else
    v-model="unit"
    :aria-label="t('DURATION_INPUT.UNIT')"
    :disabled="disabled"
    class="max-sm:!min-h-11 mb-0 text-sm disabled:outline-n-weak disabled:opacity-40"
  >
    <option :value="DURATION_UNITS.MINUTES">
      {{ t('DURATION_INPUT.MINUTES') }}
    </option>
    <option :value="DURATION_UNITS.HOURS">
      {{ t('DURATION_INPUT.HOURS') }}
    </option>
    <option :value="DURATION_UNITS.DAYS">
      {{ t('DURATION_INPUT.DAYS') }}
    </option>
  </select>
</template>
